import { useState, useEffect } from "react";
import { UserLayout } from "./components/layout/UserLayout";
import { Pagination } from "../../Components/Pagination";
import PageHero from "./components/PageHero";
import Container from "./components/Container";
import Text from "./components/Text";
import Img from "./components/Img";
import { getImg } from "../../Components/Utils";
import { ROUTE_PATHS } from "../../constants/routeConstants";
import { useFetchFrontendBrandsQuery } from "../../stores/api/pages.api";

const brandHref = (slug) => `${ROUTE_PATHS.PRODUCTS_BY_BRAND.replace(':brandSlug',slug)}`;

const FILTER_KEYS = [
  "Show all",
  "123",
  ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)),
];

const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const BrandsSkeleton = () => (
  <UserLayout>
    <Container variant="narrow" className="py-10 px-4 lg:px-8">
      <div className="flex flex-wrap justify-center gap-1.5 mb-10">
        {FILTER_KEYS.map((k) => (
          <Skeleton key={k} className="min-w-[40px] h-8" />
        ))}
      </div>
      <div className="space-y-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex gap-4 items-stretch">
            <Skeleton className="w-16 sm:w-24 h-24" />
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {[1, 2, 3, 4, 5, 6].map((j) => (
                <Skeleton key={j} className="h-28" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Container>
  </UserLayout>
);

const Brands = () => {
  const [active, setActive] = useState("Show all");
  const [page, setPage] = useState(1);
  
  // Adjusted to 24 to perfectly align with the 6-column grid structure (6 columns * 4 full rows)
  const [perPage, setPerPage] = useState(24);
  const [pageJump, setPageJump] = useState("1");
  const { data: rawBrands, isLoading } = useFetchFrontendBrandsQuery();

  // Reset to first page when the letter filter changes or pagination scale alters.
  useEffect(() => {
    setPage(1);
    setPageJump("1");
  }, [active, perPage]);

  useEffect(() => {
    setPageJump(String(page));
  }, [page]);

  if (isLoading) return <BrandsSkeleton />;

  const items = (rawBrands ?? []).map((b) => ({
    ...b,
    letter: b.name?.[0]?.toUpperCase() ?? "#",
    logo: b.image,
  }));

  const filtered =
    active === "Show all" ? items : items.filter((b) => b.letter === active);

  // Paginate the flat filtered list, then group the current page slice by letter.
  const totalPages = Math.max(Math.ceil(filtered.length / perPage), 1);
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);
  const jumpToPage = () => {
    const n = Number(pageJump);
    if (!Number.isNaN(n)) setPage(Math.min(Math.max(n, 1), totalPages));
  };

  const grouped = pageItems.reduce((acc, brand) => {
    if (!acc[brand.letter]) acc[brand.letter] = [];
    acc[brand.letter].push(brand);
    return acc;
  }, {});

  const hasAny = Object.keys(grouped).length > 0;

  return (
    <UserLayout>
      <PageHero title="Brands" crumbs={[{ label: "Brands" }]} />

      <Container variant="narrow" className="py-10 px-4 lg:px-8">
        {/* Filter pills */}
        <div className="flex flex-wrap justify-center gap-1.5 mb-10">
          {FILTER_KEYS.map((key) => {
            const isActive = active === key;
            const hasItems =
              key === "Show all" || items.some((b) => b.letter === key);
            return (
              <button
                key={key}
                onClick={() => setActive(key)}
                disabled={!hasItems}
                className={`min-w-[40px] px-3 py-1.5 text-xs font-semibold border transition-colors rounded-md
                  ${
                    isActive
                      ? "bg-gray-900 text-white border-gray-900"
                      : hasItems
                        ? "bg-white text-gray-700 border-gray-300 hover:border-gray-900 hover:text-gray-900"
                        : "bg-white text-gray-300 border-gray-200 cursor-not-allowed"
                  }`}
              >
                {key}
              </button>
            );
          })}
        </div>

        {/* Brand rows */}
        <Container
          variant="full"
          showEmptyState={!rawBrands || !hasAny}
          emptyMessage={`No brands found for "${active}"`}
        >
          <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden bg-white">
            {Object.entries(grouped).map(([letter, brands]) => (
              <div key={letter} className="flex gap-0 items-stretch">
                {/* Letter label */}
                <div className="w-16 sm:w-24 flex-shrink-0 flex items-center justify-center border-r border-gray-100 bg-gray-50/50 py-8">
                  <Text is="span" className="text-3xl font-light text-gray-400">
                    {letter}
                  </Text>
                </div>

                {/* Brand cards grid */}
                <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-0 divide-x divide-y divide-gray-100 py-0">
                  {brands.map((brand) => (
                    <Text
                      key={brand.name}
                      is="a"
                      href={brandHref(brand.slug)}
                      className="flex flex-col items-center justify-center gap-2 px-4 py-6 group hover:bg-gray-50/70 transition-colors border-t-0 first:border-t-0"
                    >
                      <div className="w-[172px] h-[91px] flex items-center justify-center overflow-hidden">
                        <Img
                          src={getImg(brand.logo)}
                          alt={brand.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <Text
                        is="span"
                        className="text-[11px] font-medium text-gray-500 group-hover:text-gray-900 transition-colors text-center"
                      >
                        {brand.name}
                      </Text>
                    </Text>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Container>

        {hasAny && totalPages > 1 && (
          <Pagination
            variant="blue"
            className="mt-8"
            currentPage={page}
            setCurrentPage={setPage}
            totalPages={totalPages}
            rowsPerPage={perPage}
            setRowsPerPage={setPerPage}
            pageJump={pageJump}
            setPageJump={setPageJump}
            jumpToPage={jumpToPage}
          />
        )}
      </Container>
    </UserLayout>
  );
};

export default Brands;